import React, { useCallback, useLayoutEffect, useRef, type ComponentType, type RefObject } from "react";
import Close from "images/close_grey.svg";
import { connect } from "react-redux";
import { toggleGridFilterModal } from "actions/grid-modal-actions";
import { flagsAndExperimentNames } from "rtr-constants";
import classNames from "classnames";
import { withOnLoadFlagsAndExperiments } from "../hoc/with-flags-and-experiments";
import { compose } from "redux";
import type { FlagsAndExperiments } from "components/types/flags-and-experiments";
import type { WorkingFilters } from "components/types/working-filters";
import type { DisplayedFilter } from "hooks/use-displayed-filters";
import { withDisplayedFilters } from "../hoc/with-displayed-filters";

type PillProps = {
  container: HTMLDivElement | null;
  filter: DisplayedFilter;
  isOpenFilterModalButtonVisible: boolean;
  isVisible: (filter: DisplayedFilter) => boolean;
  onRemoveFilter: <T extends keyof WorkingFilters>(filterKey: T, filterValue: WorkingFilters[T]) => void;
  pillCount: number;
  updatePillVisibility: (filter: DisplayedFilter, isVisible: boolean) => void;
};

const Pill = ({
  container,
  filter,
  isOpenFilterModalButtonVisible,
  isVisible,
  onRemoveFilter,
  pillCount,
  updatePillVisibility,
}: PillProps) => {
  const pillRef = useRef<HTMLLIElement>(null);

  const calculateVisibility = useCallback(() => {
    if (!container || !pillRef.current) {
      return true;
    }

    const rect = pillRef.current.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    //Check if the element is fully visible within the container within a margin of 1px
    const isLeftVisible = rect.left >= containerRect.left || Math.abs(rect.right - containerRect.right) < 1;
    const isRightVisible = rect.right <= containerRect.right || Math.abs(rect.right - containerRect.right) < 1;

    return isLeftVisible && isRightVisible;
  }, [container, pillRef]);

  useLayoutEffect(() => {
    const currentlyVisible = calculateVisibility();

    if (currentlyVisible && !isVisible(filter)) {
      updatePillVisibility(filter, true);
    } else if (!currentlyVisible && isVisible(filter)) {
      updatePillVisibility(filter, false);
    }

    //If the pill is removed from the DOM, also remove it from QuickFilter's state
    return () => updatePillVisibility(filter, false);
  }, [
    calculateVisibility,
    container,
    filter,
    isVisible,
    pillRef,
    updatePillVisibility,
    //Recalculate layout when the number of pills changes, or when we add or remove the button indicating filters are out of view
    pillCount,
    isOpenFilterModalButtonVisible,
  ]);

  const classes = classNames(
    "w-auto",
    "rounded-3xl",
    "py-1.5",
    "pl-3",
    "pr-1.5",
    "h-8",
    "flex",
    "items-center",
    "justify-center",
    "text-sm",
    "bg-offWhite",
    "inline-block",
    "whitespace-nowrap",
    "transition-opacity",
    {
      invisible: !isVisible(filter),
    }
  );

  return (
    <li ref={pillRef}>
      <button
        data-test-id="quick-filter"
        onClick={() => onRemoveFilter(filter.filterKey, filter.filterValue)}
        className={classes}>
        {filter.displayedValue}
        <Close className="w-6 h-6 ml-2.5" />
      </button>
    </li>
  );
};

export type QuickFiltersProps = {
  displayedFilters: DisplayedFilter[];
  flagsAndExperiments: FlagsAndExperiments;
  isDeadEndTextSearch?: boolean;
  removeFilter: <T extends keyof WorkingFilters>(
    filterKey: T,
    filterValue: WorkingFilters[T],
    options: { isDeadEndTextSearch?: boolean }
  ) => void;
  toggleGridFilterModal: (isModalVisible: boolean) => void;
};

type QuickFiltersState = {
  visiblePills: Set<string>;
};

export class QuickFilters extends React.Component<QuickFiltersProps, QuickFiltersState> {
  containerRef: RefObject<HTMLDivElement>;

  static readonly encodeFilterKey = (filter: DisplayedFilter) => `${filter.filterKey}:${filter.filterValue}`;

  constructor(props: QuickFiltersProps) {
    super(props);

    this.containerRef = React.createRef<HTMLDivElement>();
    this.state = {
      //A set of keys tracking filters that are visible to the user based on DOM calculations
      visiblePills: new Set(props.displayedFilters.map(QuickFilters.encodeFilterKey)),
    };
  }

  override componentDidMount() {
    window.addEventListener("resize", this.reinitializeContainerRef);
  }

  override componentWillUnmount(): void {
    window.removeEventListener("resize", this.reinitializeContainerRef);
  }

  /**
   * On resize, recreate the ref for the container.
   * The change will force the pills to recalculate their visibility state and rerender.
   */
  reinitializeContainerRef = () => {
    this.containerRef = React.createRef<HTMLDivElement>();
  };

  reserveAsFilterEnabled = (): boolean =>
    Boolean(this.props.flagsAndExperiments[flagsAndExperimentNames.RESERVE_FILTER]);

  removeFilter = <T extends keyof WorkingFilters>(filterKey: T, filterValue: WorkingFilters[T]) => {
    const { isDeadEndTextSearch, removeFilter } = this.props;
    const options = { isDeadEndTextSearch };
    removeFilter(filterKey, filterValue, options);
  };

  updatePillVisibility = (filter: DisplayedFilter, isVisible: boolean) => {
    const encoded = QuickFilters.encodeFilterKey(filter);
    const { visiblePills } = this.state;
    if (isVisible) {
      visiblePills.add(encoded);
      this.setState({ visiblePills });
    } else {
      visiblePills.delete(encoded);
      this.setState({ visiblePills });
    }
  };

  isPillVisible = (filter: DisplayedFilter) => {
    const encoded = QuickFilters.encodeFilterKey(filter);

    return this.state.visiblePills.has(encoded);
  };

  isOpenFilterModalButtonVisible() {
    const { displayedFilters } = this.props;
    const { visiblePills } = this.state;
    return visiblePills.size < displayedFilters.length;
  }

  override render() {
    const { displayedFilters, toggleGridFilterModal } = this.props;
    const { visiblePills } = this.state;

    if (!displayedFilters.length) {
      return null;
    }

    const containerClasses = classNames("flex gap-2", {
      "mt-2": !this.reserveAsFilterEnabled(),
      "ml-16": !this.reserveAsFilterEnabled() && this.isOpenFilterModalButtonVisible(),
      "mx-16": !this.reserveAsFilterEnabled() && !this.isOpenFilterModalButtonVisible(),
    });

    const openFilterModalButtonClasses = classNames(
      "flex ml-2 text-darkGray text-sm inline-block whitespace-nowrap items-center justify-center font-medium",
      {
        "mr-16": !this.reserveAsFilterEnabled(),
      }
    );

    return (
      <div ref={this.containerRef} className="flex justify-end w-full overflow-x-hidden">
        <ul className={containerClasses} data-test-id="quick-filter-container">
          {displayedFilters.map(filter => {
            return (
              <Pill
                container={this.containerRef.current}
                filter={filter}
                isVisible={this.isPillVisible}
                key={QuickFilters.encodeFilterKey(filter)}
                onRemoveFilter={this.removeFilter}
                isOpenFilterModalButtonVisible={this.isOpenFilterModalButtonVisible()}
                pillCount={displayedFilters.length}
                updatePillVisibility={this.updatePillVisibility}
              />
            );
          })}
          {this.isOpenFilterModalButtonVisible() && (
            <button className={openFilterModalButtonClasses} onClick={() => toggleGridFilterModal(true)}>
              {`+ ${displayedFilters.length - visiblePills.size} more`}
            </button>
          )}
        </ul>
      </div>
    );
  }
}

export const mapDispatchToProps = {
  toggleGridFilterModal,
};

export default compose<
  ComponentType<Omit<QuickFiltersProps, "displayedFilters" | "flagsAndExperiments" | "toggleGridFilterModal">>
>(
  withDisplayedFilters,
  withOnLoadFlagsAndExperiments(flagsAndExperimentNames.RESERVE_FILTER),
  connect(null, mapDispatchToProps)
)(QuickFilters);
